import { defineStore } from 'pinia';
import { api } from '../api/client';

const SESSION_KEY = 'tforce.user.session';

export const useAuthStore = defineStore('auth', {
  state: () => {
    // 启动时立即从 localStorage 恢复，让路由守卫能同步判断
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch (_) {}
    return {
      user: cached || null,
      initialized: !!cached,  // 有缓存即视为初始化完成（后台再异步刷新）
      loginPending: false
    };
  },
  getters: {
    isAuthenticated: (state) => !!state.user,
    isAdmin: (state) => !!(state.user && state.user.isAdmin)
  },
  actions: {
    /**
     * 检查会话：/api/auth/me 返 { username, role, isAdmin, authenticated, ... }
     * 注意：此 action 不阻塞路由 —— 缓存的 user 已经在 state init 时恢复
     */
    async checkSession() {
      try {
        const data = await api.get('/api/auth/me');
        if (data && data.username) {
          this._setUser({
            userId: data.userId,
            username: data.username,
            nickname: data.nickname || data.username,
            role: data.role,
            isAdmin: data.role === 'ADMIN' || data.isAdmin === true || data.isVip === true
          });
          this.initialized = true;
          return true;
        }
        throw new Error('未登录');
      } catch (err) {
        this.user = null;
        this.initialized = true;
        try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
        return false;
      }
    },
    /**
     * 登录：POST /login（JSON body）。后端返 { code: 0, data: { ...user } } 表示成功
     */
    async login(username, password) {
      this.loginPending = true;
      try {
        const res = await fetch('/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ username, password }),
          credentials: 'include'
        });
        let data = null;
        try { data = await res.json(); } catch (_) {}
        if (res.ok && data && data.code === 0 && data.data) {
          const u = data.data;
          this._setUser({
            userId: u.userId,
            username: u.username,
            nickname: u.nickname || u.username,
            role: u.role,
            isAdmin: u.role === 'ADMIN' || u.isVip === true
          });
          return { ok: true };
        }
        if (res.status === 401 || (data && data.code === 401)) {
          return { ok: false, message: (data && data.message) || '账号或密码错误' };
        }
        if (res.status === 403 || (data && data.code === 403)) {
          return { ok: false, message: (data && data.message) || '账户已被禁用' };
        }
        if (res.status === 429) return { ok: false, message: '尝试次数过多，请稍后再试' };
        return { ok: false, message: (data && data.message) || ('登录失败 (HTTP ' + res.status + ')') };
      } catch (err) {
        return { ok: false, message: '网络异常：' + (err.message || err) };
      } finally {
        this.loginPending = false;
      }
    },
    _setUser(user) {
      this.user = user;
      try { localStorage.setItem(SESSION_KEY, JSON.stringify(user)); } catch (_) {}
    },
    /**
     * 登出：POST /logout（JSON），后端清 session
     */
    async logout() {
      try {
        await fetch('/logout', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          credentials: 'include'
        });
      } catch (_) {}
      this.user = null;
      try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
    }
  }
});
